import { Button } from '@repo/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select';
import { type Table } from '@tanstack/react-table';
import { useTranslations } from 'next-intl';

interface PaginationControlsProps<T> {
  table: Table<T>;
  pageSizeOptions?: number[];
  className?: string;
  isLoading?: boolean;
}

const PaginationControls = <T,>({
  table,
  pageSizeOptions = [10, 20, 30, 40, 50],
  className,
  isLoading,
}: PaginationControlsProps<T>) => {
  const t = useTranslations('components.table');

  const handlePageSizeChange = (value: string) => {
    const newSize = Number(value);
    table.setPageSize(newSize);
  };

  const handleFirstPage = () => {
    table.setPageIndex(0);
  };

  const handlePreviousPage = () => {
    table.previousPage();
  };

  const handleNextPage = () => {
    table.nextPage();
  };

  const handleLastPage = () => {
    table.setPageIndex(table.getPageCount() - 1);
  };

  return (
    <div className={`flex items-center justify-start space-x-4 p-4 lg:justify-end rtl:space-x-reverse ${className}`}>
      <div className="rtl:gap-x-reverse flex items-center gap-4">
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={handlePageSizeChange}
        >
          <SelectTrigger className="w-40 vt-input-default-bg-normal vt-input-default-stroke-normal vt-input-text-size-normal focus:vt-input-default-bg-focus focus:vt-input-default-text-focus focus:vt-input-default-stroke-focus">
            <SelectValue placeholder="Show items" />
          </SelectTrigger>
          <SelectContent className="!vt-surface-default-bg">
            {pageSizeOptions.map((pageSize) => (
              <SelectItem
                key={pageSize}
                value={pageSize.toString()}
                className="vt-text-size-small-sm vt-body-text-dark"
              >
                {t('show-items', { pageSize })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="rtl:gap-x-reverse flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleFirstPage}
            disabled={!table.getCanPreviousPage() || !table.getRowCount() || isLoading}
          >
            {t('first')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePreviousPage}
            disabled={!table.getCanPreviousPage() || !table.getRowCount() || isLoading}
          >
            {t('previous')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleNextPage}
            disabled={!table.getCanNextPage() || !table.getRowCount() || isLoading}
          >
            {t('next')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleLastPage}
            disabled={!table.getCanNextPage() || !table.getRowCount() || isLoading}
          >
            {t('last')}
          </Button>
        </div>
      </div>

      <div className="rtl:gap-x-reverse flex items-center gap-2 vt-text-size-small-sm vt-body-text-dark">
        <span>
          {t('page-info', { page: table.getState().pagination.pageIndex + 1, totalPages: table.getPageCount() })}
        </span>
        <span className="vt-body-text-weak">|</span>
        <span>{t('total-items', { total: table.getRowCount() })}</span>
      </div>
    </div>
  );
};

export default PaginationControls;
